<template>
  <a-form-field v-bind="formFieldAttrs">
    <div class="form-upload">

      <ul
        v-if="fileList.length"
        class="form-upload__list form-upload__list--uploaded"
      >
        <li
          v-for="(file, index) in fileList"
          v-if="file !== ''"
          :key="`${index}-${file}`"
          class="form-upload__item"
        >
          {{ file }}
          <button
            :aria-label="`Datei „${file}“ entfernen`"
            class="form-upload__remove"
            type="button"
            @click="deleteFile(file)"
          />
        </li>
      </ul>

      <template v-if="$attrs.maximum === undefined || $attrs.maximum === 0 || fileList.length < $attrs.maximum">
        <div
          class="form-upload__dropzone"
        >

          <p
            v-if="$attrs.maximum === undefined || $attrs.maximum === 0 || fileData.length < $attrs.maximum"
            class="form-upload__text"
          >
            Datei hinzufügen und Upload starten
          </p>

          <ul
            v-if="fileData.length"
            class="form-upload__list"
          >
            <li
              v-for="file in fileData"
              :key="file.id"
              :class="{ 'has-error': file.error }"
              class="form-upload__item"
            >
              <span class="form-upload__file-name">
                {{ file.name }} ({{ file.size | formatFileSize }})
              </span>

              <span
                v-if="file.error"
                class="form-upload__file-error"
              >
                {{ fileErrors[file.error] }}
              </span>

              <span
                v-if="file.active"
                :style="{ transform: `scaleX(${Math.floor(file.progress) / 100})` }"
                class="form-upload__file-progress"
              >
                <span class="sr-only">
                  Upload-Fortschritt: {{ file.progress }}%
                </span>
              </span>

              <button
                :aria-label="`Datei „${file.file.name}“ entfernen`"
                class="form-upload__remove"
                type="button"
                @click="removeFile(file.id)"
              />

            </li>
          </ul>

        </div>

        <vue-upload-component
          ref="upload"
          :drop="true"
          :input-id="id"
          :post-action="uploadUri"
          :headers="{ Authorization: 'Token ' + token }"
          :multiple="false"
          :size="this.$attrs.size * 1024"
          v-bind="$attrs"
          v-model="fileData"
          @input-file="inputFile"
          @input-filter="inputFilter"
        >
          Datei hinzufügen
        </vue-upload-component>

        <c-button
          v-if="!$refs.upload || !$refs.upload.active"
          :disabled="!fileData.length"
          type="button"
          @click="$refs.upload.active = true"
        >
          Upload starten
        </c-button>

        <c-button
          v-else
          @click="$refs.upload.active = false"
        >
          Upload abbrechen
        </c-button>
      </template>
    </div>

  </a-form-field>
</template>

<script>
import AFormField from '@/components/form/atoms/AFormField'
import BFormInput from '@/components/form/base/BFormInput'
import CBusy from '@/components/CBusy'
import CButton from '@/components/CButton'

import formatFileSize from '@/functions/formatFileSize'
import sessionToken from '@/functions/sessionToken'

export default {
  name: 'CFormUpload',

  components: {
    CButton,
    AFormField,
    VueUploadComponent: () => ({
      component: import('vue-upload-component'),
      loading: CBusy
    })
  },

  filters: {
    formatFileSize
  },

  extends: BFormInput,

  props: {
    id: {
      type: String,
      require: true
    },
    uploadUri: {
      type: String,
      required: true
    },
    deleteUri: {
      type: String,
      required: true
    },
    value: {
      type: String
    }
  },

  data () {
    return {
      fileErrors: {
        abort: 'Abgebrochen',
        denied: 'Nicht erlaubt',
        extension: 'Nicht erlaubte Dateiendung',
        network: 'Keine Internetverbindung',
        server: 'Server-Fehler',
        size: 'Datei zu groß',
        timeout: 'Upload dauert zu lange'
      },
      fileList: [],
      fileData: [],
      token: sessionToken.get()
    }
  },

  computed: {
    uploadedFiles () {
      return this.value ? this.value.split(',') : []
    }
  },

  mounted () {
    this.fileList = this.uploadedFiles
  },

  methods: {
    inputFilter (newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Check for filename
        for (let f of this.fileList) {
          if (f === newFile.name) {
            this.$store.commit('modal/open', {
              title: 'Dateiname doppelt',
              content: 'Es wurde bereits eine Datei mit dem Namen "' + newFile.name + '" hochgeladen.',
              buttons: [{
                text: 'Ok'
              }]
            })
            return prevent()
          }
        }

        for (let f of this.fileData) {
          if (f.name === newFile.name) {
            this.$store.commit('modal/open', {
              title: 'Dateiname doppelt',
              content: 'Es ist bereits eine Datei mit dem Namen "' + f.name + '" für den Upload ausgewählt.',
              buttons: [{
                text: 'Ok'
              }]
            })
            return prevent()
          }
        }

        // Check for file size
        if (this.$attrs.size > 0 && newFile.size > this.$attrs.size * 1024) {
          this.$store.commit('modal/open', {
            title: 'Datei zu groß',
            content: 'Die Datei ist zu groß.',
            buttons: [{
              text: 'Ok'
            }]
          })
          return prevent()
        }

        // Check for allowed file extensions
        let ext = newFile.name.split('.').pop()
        if (this.$attrs.extensions &&
            !this.$attrs.extensions.includes('*') &&
            !this.$attrs.extensions.includes(ext.toLowerCase())) {
          this.$store.commit('modal/open', {
            title: 'Ungültige Datei',
            content: 'Das Dateiformat ist nicht gültig. Erlaubt sind ' + this.$attrs.extensions.join(', '),
            buttons: [{
              text: 'Ok'
            }]
          })
          return prevent()
        }
      }
    },

    inputFile (newFile, oldFile) {
      if (newFile && oldFile) {
        if (newFile.success && !oldFile.success) {
          // Move file to file list
          this.fileList.push(newFile.name)
          this.$refs.upload.remove(newFile)
          this.$emit('complete', this.fileList.join(','))
        }
      }
    },

    removeFile (file) {
      this.$refs.upload.remove(file)
    },

    deleteFile (file) {
      this.$store.commit('modal/open', {
        title: 'Datei löschen',
        content: `Wollen Sie die Datei „${file}“ wirklich löschen?`,
        buttons: [{
          text: 'Ja',
          handler: () => {
            this.$api.get(this.deleteUri, { params: { file } })
              .then(response => {
                if (response.status === 204) {
                  let fIndex = this.fileList.indexOf(file)
                  if (fIndex > -1) {
                    this.fileList.splice(fIndex, 1)
                    this.$emit('deleted', this.fileList.join(','))
                  }
                }
              })
          }
        }, {
          text: 'Nein'
        }]
      })
    }
  }
}
</script>

<style scoped>
.form-upload {
  flex-grow: 1;
}

.form-upload__dropzone {
  margin-bottom: 10px;
  padding: 12px;

  font-style: italic;

  background: #fff;
  border: 2px solid var(--color-brand);
}

.form-upload__text {
  margin: 0 0 10px;
}

.form-upload__text:last-child {
  margin-bottom: 0;
}

.form-upload__list {
  list-style: none;

  margin: 0 -4px;
  padding: 0;
}

.form-upload__list--uploaded {
  margin-bottom: 20px;
}

.form-upload__item {
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-left: 16px;
  overflow: hidden;

  line-height: 1.4;

  background-color: var(--color-brand-pale);
  border-radius: 18px;

  transition:
    color 300ms,
    background-color 300ms;
}

.form-upload__item:last-child {
  margin-bottom: 0;
}

.form-upload__item.has-error {
  color: #fff;
  background-color: var(--color-error);
}

.form-upload__file-name {
  flex-grow: 1;
}

.form-upload__file-progress {
  position: absolute;
  width: 100%;
  height: 3px;
  bottom: 0;
  left: 0;
  transform-origin: left;

  background-color: var(--color-brand);

  transition: transform 300ms;
}

.form-upload__remove {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;

  background: url("~@/assets/form-remove.svg") center / 16px no-repeat;
  border: 0;
}

.file-uploads {
  margin-right: 10px;
  padding: 8px 16px;
  vertical-align: middle;

  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-brand);

  border: 2px solid var(--color-brand);
  border-radius: 20px;

  transition:
    color 300ms,
    background-color 300ms;
}

.file-uploads:hover,
.file-uploads:focus-within {
  color: #fff;
  background-color: var(--color-brand);
}

.file-uploads:focus-within {
  outline: 2px dotted var(--color-brand);
  outline-offset: 2px;
}
</style>
